import React, { useState, useEffect } from 'react';
import { adminAPI } from './services/api';
import './BlockedDatesTab.css';

interface BlockedDateEntry {
  date: string;             // "YYYY-MM-DD"
  reason: string;           // Müşteriye gösterilen kısa neden
  note: string;             // Yalnızca admin görebilir (dahili not)
  affects: string[];        // ['breakfast', 'dinner', 'rooms', 'sunbed']
}

const SERVICE_OPTIONS = [
  { value: 'breakfast', label: 'Kahvaltı' },
  { value: 'dinner',    label: 'Akşam Yemeği' },
  { value: 'rooms',     label: 'Oda Rezervasyonu' },
  { value: 'sunbed',    label: 'Şezlong' },
];

const today = new Date().toISOString().split('T')[0];

const BlockedDatesTab: React.FC = () => {
  const [blockedDates, setBlockedDates] = useState<BlockedDateEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [message, setMessage]   = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // New-date form
  const [newDate,    setNewDate]    = useState('');
  const [newReason,  setNewReason]  = useState('');
  const [newNote,    setNewNote]    = useState('');
  const [newAffects, setNewAffects] = useState<string[]>(['breakfast', 'dinner', 'rooms']);

  // Edit mode
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [editDate,    setEditDate]    = useState('');
  const [editReason,  setEditReason]  = useState('');
  const [editNote,    setEditNote]    = useState('');
  const [editAffects, setEditAffects] = useState<string[]>([]);

  useEffect(() => {
    loadBlockedDates();
  }, []);

  const loadBlockedDates = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.get('/admin/settings/blocked-dates');
      if (res.data.success) {
        const sorted = [...(res.data.data as BlockedDateEntry[])].sort((a, b) =>
          a.date.localeCompare(b.date)
        );
        setBlockedDates(sorted);
      }
    } catch (err: any) {
      showMessage('error', 'Kapalı tarihler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const save = async (updated: BlockedDateEntry[]) => {
    setSaving(true);
    try {
      const res = await adminAPI.put('/admin/settings/blocked-dates', { dates: updated });
      if (res.data.success) {
        setBlockedDates([...updated].sort((a, b) => a.date.localeCompare(b.date)));
        showMessage('success', 'Kapalı tarihler başarıyla kaydedildi');
      } else {
        showMessage('error', res.data.message || 'Kayıt başarısız');
      }
    } catch (err: any) {
      showMessage('error', err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    if (!newDate) {
      showMessage('error', 'Lütfen bir tarih seçin');
      return;
    }
    if (newAffects.length === 0) {
      showMessage('error', 'En az bir hizmet seçin');
      return;
    }
    if (blockedDates.some(e => e.date === newDate)) {
      showMessage('error', 'Bu tarih zaten eklenmiş. Önce mevcut kaydı silin.');
      return;
    }

    const entry: BlockedDateEntry = {
      date:    newDate,
      reason:  newReason.trim(),
      note:    newNote.trim(),
      affects: newAffects
    };

    const updated = [...blockedDates, entry];
    save(updated);

    // Reset form
    setNewDate('');
    setNewReason('');
    setNewNote('');
    setNewAffects(['breakfast', 'dinner', 'rooms']);
  };

  const handleRemove = (date: string) => {
    if (!window.confirm(`"${formatDate(date)}" tarihini kapalı tarihlerden kaldırmak istediğinize emin misiniz?`)) return;
    const updated = blockedDates.filter(e => e.date !== date);
    save(updated);
  };

  const toggleAffects = (value: string) => {
    setNewAffects(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const toggleEditAffects = (value: string) => {
    setEditAffects(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handleEdit = (entry: BlockedDateEntry) => {
    setEditingDate(entry.date);
    setEditDate(entry.date);
    setEditReason(entry.reason);
    setEditNote(entry.note);
    setEditAffects([...entry.affects]);
  };

  const handleCancelEdit = () => {
    setEditingDate(null);
    setEditDate('');
    setEditReason('');
    setEditNote('');
    setEditAffects([]);
  };

  const handleSaveEdit = () => {
    if (!editDate) {
      showMessage('error', 'Lütfen bir tarih seçin');
      return;
    }
    if (editAffects.length === 0) {
      showMessage('error', 'En az bir hizmet seçin');
      return;
    }

    // Tarih değiştiyse ve yeni tarih başka bir kayıtta varsa hata ver
    if (editDate !== editingDate && blockedDates.some(e => e.date === editDate)) {
      showMessage('error', 'Bu tarih zaten mevcut. Lütfen farklı bir tarih seçin.');
      return;
    }

    let updated: BlockedDateEntry[];
    
    if (editDate === editingDate) {
      // Tarih değişmedi, sadece diğer alanları güncelle
      updated = blockedDates.map(e =>
        e.date === editingDate
          ? { ...e, reason: editReason.trim(), note: editNote.trim(), affects: editAffects }
          : e
      );
    } else {
      // Tarih değişti, eski kaydı sil yenisini ekle
      updated = blockedDates
        .filter(e => e.date !== editingDate)
        .concat({
          date: editDate,
          reason: editReason.trim(),
          note: editNote.trim(),
          affects: editAffects
        });
    }

    save(updated);
    setEditingDate(null);
    setEditDate('');
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('tr-TR', {
        year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="blocked-dates-tab">
        <div className="loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="blocked-dates-tab">
      <div className="blocked-dates-header">
        <h2>🚫 Kapalı Tarih Yönetimi</h2>
        <p className="blocked-dates-description">
          Düğün organizasyonları veya özel etkinlikler gibi durumlarda belirli tarihlerde
          kahvaltı, akşam yemeği ve/veya oda rezervasyonu almak istemeyebilirsiniz.
          Aşağıdan kapalı tarihleri yönetebilirsiniz.
        </p>
      </div>

      {message && (
        <div className={`blocked-dates-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Add new blocked date */}
      <div className="blocked-dates-add-card">
        <h3>➕ Yeni Kapalı Tarih Ekle</h3>
        <div className="blocked-dates-form">
          <div className="form-row">
            <div className="form-group">
              <label>Tarih *</label>
              <input
                type="date"
                value={newDate}
                min={today}
                onChange={e => setNewDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Müşteri Mesajı <span className="label-hint">(müşteriye gösterilir)</span></label>
              <input
                type="text"
                value={newReason}
                onChange={e => setNewReason(e.target.value)}
                placeholder="ör. Özel etkinlik"
                className="form-input"
                maxLength={100}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Dahili Not <span className="label-hint">(yalnızca admin görür)</span></label>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="ör. Ahmet Bey'in düğünü — 120 kişilik organizasyon, tüm tesis kapalı"
              className="form-input form-textarea"
              rows={2}
              maxLength={300}
            />
          </div>

          <div className="form-group">
            <label>Etkilenen Hizmetler *</label>
            <div className="affects-checkboxes">
              {SERVICE_OPTIONS.map(opt => (
                <label key={opt.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newAffects.includes(opt.value)}
                    onChange={() => toggleAffects(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <button
            className="btn-add-blocked"
            onClick={handleAdd}
            disabled={saving}
          >
            {saving ? 'Kaydediliyor...' : '➕ Kapalı Tarih Ekle'}
          </button>
        </div>
      </div>

      {/* Existing blocked dates */}
      <div className="blocked-dates-list-section">
        <h3>📋 Mevcut Kapalı Tarihler
          <span className="count-badge">{blockedDates.length}</span>
        </h3>

        {blockedDates.length === 0 ? (
          <div className="no-blocked-dates">
            Henüz kapalı tarih eklenmemiş.
          </div>
        ) : (
          <div className="blocked-dates-table-wrapper">
            <table className="blocked-dates-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Müşteri Mesajı</th>
                  <th>Dahili Not 🔒</th>
                  <th>Etkilenen Hizmetler</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {blockedDates.map(entry => {
                  const isEditing = editingDate === entry.date;
                  
                  return (
                    <tr key={entry.date} className={`${entry.date < today ? 'past-date' : ''} ${isEditing ? 'editing-row' : ''}`}>
                      <td className="date-cell">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editDate}
                            min={today}
                            onChange={e => setEditDate(e.target.value)}
                            className="form-input edit-input"
                          />
                        ) : (
                          <>
                            <span className="date-badge">{formatDate(entry.date)}</span>
                            {entry.date < today && <span className="past-label">geçmiş</span>}
                          </>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editReason}
                            onChange={e => setEditReason(e.target.value)}
                            placeholder="ör. Özel etkinlik"
                            className="form-input edit-input"
                            maxLength={100}
                          />
                        ) : (
                          entry.reason || <span className="no-reason">—</span>
                        )}
                      </td>
                      <td className="note-cell">
                        {isEditing ? (
                          <textarea
                            value={editNote}
                            onChange={e => setEditNote(e.target.value)}
                            placeholder="Dahili not"
                            className="form-input edit-textarea"
                            rows={2}
                            maxLength={300}
                          />
                        ) : (
                          entry.note || <span className="no-reason">—</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="affects-checkboxes-inline">
                            {SERVICE_OPTIONS.map(opt => (
                              <label key={opt.value} className="checkbox-label-inline">
                                <input
                                  type="checkbox"
                                  checked={editAffects.includes(opt.value)}
                                  onChange={() => toggleEditAffects(opt.value)}
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="affects-tags">
                            {SERVICE_OPTIONS
                              .filter(o => entry.affects.includes(o.value))
                              .map(o => (
                                <span key={o.value} className={`affects-tag affects-${o.value}`}>
                                  {o.label}
                                </span>
                              ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="edit-actions">
                            <button
                              className="btn-save-edit"
                              onClick={handleSaveEdit}
                              disabled={saving}
                              title="Kaydet"
                            >
                              Kaydet
                            </button>
                            <button
                              className="btn-cancel-edit"
                              onClick={handleCancelEdit}
                              disabled={saving}
                              title="İptal"
                            >
                              İptal
                            </button>
                          </div>
                        ) : (
                          <div className="row-actions">
                            <button
                              className="btn-edit"
                              onClick={() => handleEdit(entry)}
                              disabled={saving || editingDate !== null}
                              title="Düzenle"
                            >
                              Düzenle
                            </button>
                            <button
                              className="btn-remove"
                              onClick={() => handleRemove(entry.date)}
                              disabled={saving || editingDate !== null}
                              title="Kaldır"
                            >
                              Sil
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedDatesTab;
